import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { BRAND, PRODUCTS } from "../data/products";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "./Logo";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Products", to: "/products", hasMega: true },
  { label: "Projects", to: "/projects" },
  { label: "Dealer", to: "/dealer" },
  { label: "Insights", to: "/insights" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMegaOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-dark border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <Logo
            variant="mark"
            theme="light"
            className="h-9 md:h-10 lg:h-11 w-auto transition-transform group-hover:scale-105"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((item) => (
            <div
              key={item.to}
              className="relative"
              onMouseEnter={() => item.hasMega && setMegaOpen(true)}
              onMouseLeave={() => item.hasMega && setMegaOpen(false)}
            >
              <NavLink
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-2 font-heading text-sm uppercase tracking-wider transition-colors flex items-center gap-1 ${
                    isActive ? "text-liftile-orange" : "text-liftile-cream/85 hover:text-liftile-orange"
                  }`
                }
              >
                {item.label}
                {item.hasMega && <ChevronDown className="w-3 h-3" />}
              </NavLink>

              {item.hasMega && (
                <AnimatePresence>
                  {megaOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute top-full left-1/2 -translate-x-1/2 w-[640px] glass-dark border border-white/10 p-6 grid grid-cols-2 gap-2"
                    >
                      {PRODUCTS.map((p) => (
                        <Link
                          key={p.slug}
                          to={`/products/${p.slug}`}
                          data-testid={`mega-product-${p.slug}`}
                          className="px-3 py-2 hover:bg-liftile-orange/10 transition-colors group flex flex-col"
                        >
                          <span className="font-heading text-sm text-liftile-cream group-hover:text-liftile-orange transition-colors">
                            {p.name}
                          </span>
                          <span className="text-xs text-liftile-cream/50 mt-0.5">{p.tagline}</span>
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
            data-testid="nav-phone"
            className="flex items-center gap-2 text-liftile-cream/80 hover:text-liftile-orange transition"
          >
            <Phone className="w-4 h-4 text-liftile-orange" />
            <span className="font-heading text-sm">{BRAND.phone}</span>
          </a>
          <Link to="/contact" data-testid="nav-cta" className="btn-primary text-xs px-5 py-3">
            Get A Quote
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-liftile-cream p-2"
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden bg-liftile-matte-black border-t border-white/5 overflow-hidden"
          >
            <div className="container-x py-6 flex flex-col gap-1">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `py-3 font-heading text-base uppercase tracking-wider border-b border-white/5 ${
                      isActive ? "text-liftile-orange" : "text-liftile-cream"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Link to="/contact" data-testid="mobile-nav-cta" className="btn-primary mt-4 self-start">
                Get A Quote
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
